<template>
  <div v-if="file" class="training-file-viewer">
    <video
      v-if="$isFileVideo(file)"
      ref="video"
      :key="file.id"
      controls
      preload="metadata"
      :controlslist="watchRequired ? 'nodownload' : false"
      :src="fileUrl({ contentDisposition: 'inline' })"
      class="w-100 training-file-viewer-media"
      @loadedmetadata="onLoadedMetadata"
      @timeupdate="onTimeUpdate"
      @seeking="onSeeking"
      @pause="saveProgress"
      @ended="onEnded"
    />
    <img
      v-else-if="$isFileImage(file)"
      :src="fileUrl({ contentDisposition: 'inline' })"
      :alt="file.name"
      class="w-100 training-file-viewer-media"
    />
    <iframe
      v-else-if="isPdf"
      :key="file.id"
      :src="fileUrl({ contentDisposition: 'inline' })"
      :title="file.name"
      class="w-100 training-file-viewer-pdf"
    />
    <div v-else class="text-center py-5">
      <i class="fa fa-3x fa-file-alt text-muted mb-3"></i>
      <p class="mb-3">{{ $t("TRAININGS.NOT_PREVIEWABLE") }}</p>
      <a :href="fileUrl()" class="btn btn-primary btn-sm">
        <i class="fa fa-download mr-1"></i>
        {{ $t("TRAININGS.DOWNLOAD") }}
      </a>
    </div>

    <div v-if="watchRequired" class="mt-2">
      <base-progress type="primary" :value="watchedPercent" :height="6" />
      <small class="text-muted">
        <i class="fa fa-lock mr-1"></i>
        {{ $t("TRAININGS.WATCH_REQUIRED", { percent: watchedPercent }) }}
      </small>
    </div>

    <div class="d-flex flex-wrap justify-content-between mt-2">
      <small class="text-muted">
        {{ file.human_readable_size }}
        <span v-if="file.excerpt"> · {{ file.excerpt }}</span>
      </small>
      <!-- Hidden while the video must be watched: a viewing outside the player is not counted. -->
      <a
        v-if="!watchRequired"
        :href="fileUrl(isPreviewable ? { contentDisposition: 'inline' } : {})"
        target="_blank"
        rel="noopener"
        class="small"
      >
        <i class="fa fa-external-link-alt mr-1"></i>
        {{ $t("TRAININGS.OPEN_IN_NEW_TAB") }}
      </a>
    </div>
  </div>
</template>

<script>
import { buildFileUrl } from "@/helpers/fileUrl";

// The furthest point reached is saved every 15 seconds of video, at pause, at
// the end and when the file is left.
const SAVE_INTERVAL_SECONDS = 15;
// Same margin as TrainingService::END_MARGIN_SECONDS.
const END_MARGIN_SECONDS = 3;

function newTracking(file) {
  const watched = file?.watched_seconds || 0;
  return {
    fileId: file?.id ?? null,
    required: !!file?.must_watch && !file?.completed_at,
    furthest: watched,
    saved: watched,
    duration: file?.duration_seconds || 0,
  };
}

/**
 * Lecteur d'un fichier de formation. Quand la vidéo doit être regardée en
 * entier (must_watch), l'avance au-delà du point le plus loin atteint est
 * bloquée, la lecture reprend à ce point et la vidéo se coche à la fin.
 */
export default {
  name: "training-file-viewer",

  props: {
    file: {
      type: Object,
      default: null,
      description:
        "File from the trainings API (url, name, file_extension, must_watch, watched_seconds)",
    },
  },

  data() {
    return {
      tracking: newTracking(this.file),
      completing: false,
    };
  },

  computed: {
    isPdf() {
      return this.file?.file_extension?.toLowerCase() === "pdf";
    },
    isPreviewable() {
      return (
        this.$isFileVideo(this.file) ||
        this.$isFileImage(this.file) ||
        this.isPdf
      );
    },
    watchRequired() {
      return !!this.file?.must_watch && !this.file?.completed_at;
    },
    watchedPercent() {
      const { furthest, duration } = this.tracking;
      if (!duration) {
        return 0;
      }
      return Math.min(100, Math.round((furthest / duration) * 100));
    },
  },

  watch: {
    "file.id"() {
      this.saveProgress();
      this.tracking = newTracking(this.file);
    },
  },

  beforeDestroy() {
    this.saveProgress();
  },

  methods: {
    fileUrl(options = {}) {
      return buildFileUrl(this.file?.url, this.file?.name, options);
    },

    /**
     * Furthest point reached without skipping: the ranges really played
     * (seeks excluded) that start where the employee already was.
     */
    reachedPosition(video) {
      let furthest = this.tracking.furthest;
      const { played } = video;
      for (let index = 0; index < played.length; index += 1) {
        if (played.start(index) <= furthest + 1) {
          furthest = Math.max(furthest, played.end(index));
        }
      }
      return furthest;
    },

    onLoadedMetadata() {
      const video = this.$refs.video;
      if (!video || !Number.isFinite(video.duration)) {
        return;
      }
      this.tracking.duration = video.duration;
      const { furthest } = this.tracking;
      if (
        this.watchRequired &&
        furthest > 1 &&
        furthest < video.duration - END_MARGIN_SECONDS
      ) {
        video.currentTime = furthest;
      }
    },

    onTimeUpdate() {
      const video = this.$refs.video;
      if (!this.watchRequired || !video) {
        return;
      }
      this.tracking.furthest = this.reachedPosition(video);
      if (
        this.tracking.furthest - this.tracking.saved >=
        SAVE_INTERVAL_SECONDS
      ) {
        this.saveProgress();
      }
    },

    onSeeking() {
      const video = this.$refs.video;
      if (!this.watchRequired || !video) {
        return;
      }
      this.tracking.furthest = this.reachedPosition(video);
      if (video.currentTime > this.tracking.furthest + 1) {
        video.currentTime = this.tracking.furthest;
      }
    },

    async onEnded() {
      const video = this.$refs.video;
      if (!this.watchRequired || !video || this.completing) {
        return;
      }
      this.completing = true;
      this.tracking.furthest = this.reachedPosition(video);
      try {
        await this.saveProgress();
        await this.$store.dispatch("trainings/setCompleted", {
          fileId: this.tracking.fileId,
          completed: true,
        });
        this.$notify({
          type: "success",
          message: this.$t("TRAININGS.VIDEO_COMPLETED"),
        });
      } catch (error) {
        this.$showRequestError(error);
      }
      this.completing = false;
    },

    async saveProgress() {
      // Kept by reference: the employee may open another file meanwhile.
      const tracking = this.tracking;
      const { fileId, required, furthest, saved, duration } = tracking;
      if (!required || !duration || furthest <= saved) {
        return;
      }
      tracking.saved = furthest;
      try {
        await this.$store.dispatch("trainings/saveProgress", {
          fileId,
          position: furthest,
          duration,
        });
      } catch (error) {
        // Sent again at the next interval, and at the end of the video.
        tracking.saved = Math.min(tracking.saved, saved);
        if (this.completing) {
          throw error;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.training-file-viewer-media {
  max-height: 65vh;
  background: #000;
  border-radius: 0.375rem;
}
.training-file-viewer-pdf {
  height: 70vh;
  border: 0;
}
</style>
